import type {
  BBox,
  Me,
  MyRating,
  NewPlace,
  PlaceDetail,
  PlaceSummary,
  Rating,
  RatingInput,
  Stats,
} from "./types";

const BASE = "/api/v1";

/** An error returned by the API (or a transport failure, with status 0). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly field?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; field?: string; details?: Record<string, unknown> };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "A szerver nem érhető el.");
  }

  if (res.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await res.text();
  if (text !== "") {
    try {
      parsed = JSON.parse(text);
    } catch {
      if (res.ok) throw new ApiError(res.status, "bad_response", "Értelmezhetetlen válasz a szervertől.");
    }
  }

  if (!res.ok) {
    const e = (parsed as ErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      e?.code ?? "unknown",
      e?.message ?? "Ismeretlen hiba",
      e?.field,
      e?.details,
    );
  }
  return parsed as T;
}

/** Hungarian, user-facing text for an error. Falls back to a generic message. */
export function errorText(err: unknown): string {
  if (!(err instanceof ApiError)) return "Váratlan hiba történt.";
  switch (err.code) {
    case "network":
      return "A szerver nem érhető el. Ellenőrizd a kapcsolatot.";
    case "validation":
      return validationText(err);
    case "no_session":
      return "Előbb add meg a becenevedet.";
    case "banned":
      return "Ez az eszköz ki van tiltva.";
    case "forbidden":
      return "Ehhez nincs jogosultságod.";
    case "edit_locked":
      return "Ezt a helyet már nem szerkesztheted, mert mások is értékelték.";
    case "place_not_found":
    case "not_found":
      return "Ez a hely nem található.";
    case "rating_not_found":
      return "Még nem értékelted ezt a helyet.";
    case "duplicate_place":
      return "Úgy tűnik, ez a hely már szerepel a térképen.";
    case "place_merged":
      return "Ezt a helyet összevonták egy másikkal.";
    case "out_of_area":
      return "Ez a pont kívül esik a lefedett területen.";
    case "bbox_too_large":
      return "Nagyíts rá a térképre a helyek betöltéséhez.";
    case "body_too_large":
      return "Túl hosszú a beküldött szöveg.";
    case "rate_limited":
      return "Túl sok kérés egymás után. Várj egy percet.";
    case "quota_exceeded":
      return "Elérted a napi limitet. Próbáld újra holnap.";
    default:
      return "Hiba történt. Próbáld újra.";
  }
}

/** Validation messages come from the API in English; map the known fields to Hungarian. */
function validationText(err: ApiError): string {
  switch (err.field) {
    case "nickname":
      return "A becenév 2–24 karakter legyen.";
    case "name":
      return "A hely neve 2–80 karakter legyen.";
    case "address":
      return "A cím legfeljebb 200 karakter lehet.";
    case "comment":
      return "A megjegyzés legfeljebb 280 karakter lehet.";
    case "crispiness":
    case "taste":
    case "value":
      return "Mindhárom szempontot 1–5 között kell értékelni.";
    case "lat":
    case "lon":
      return "Érvénytelen koordináta.";
    default:
      return "Érvénytelen adat.";
  }
}

export const api = {
  // Session / identity
  me: () => request<Me>("GET", "/me"),
  createSession: (nickname: string) => request<Me>("POST", "/session", { nickname }),
  setNickname: (nickname: string) => request<Me>("PATCH", "/me", { nickname }),
  myRatings: () => request<{ ratings: MyRating[] }>("GET", "/me/ratings"),

  // Places
  placesInBBox: (b: BBox, limit = 300) =>
    request<{ places: PlaceSummary[] }>(
      "GET",
      `/places?bbox=${bboxParam(b)}&limit=${limit}`,
    ),
  place: (id: string) => request<PlaceDetail>("GET", `/places/${id}`),
  createPlace: (p: NewPlace) => request<PlaceDetail>("POST", "/places", p),

  // Ratings
  ratings: (placeId: string, cursor: string) =>
    request<{ ratings: Rating[]; next_cursor: string | null }>(
      "GET",
      `/places/${placeId}/ratings?cursor=${encodeURIComponent(cursor)}`,
    ),
  putMyRating: (placeId: string, r: RatingInput) =>
    request<{ rating: Rating; stats: Stats }>("PUT", `/places/${placeId}/ratings/me`, r),
  deleteMyRating: (placeId: string) =>
    request<void>("DELETE", `/places/${placeId}/ratings/me`),
};

function bboxParam(b: BBox): string {
  return [b.minLon, b.minLat, b.maxLon, b.maxLat].map((n) => n.toFixed(6)).join(",");
}
